<script setup>
import { computed, ref, watch } from 'vue'
import { useBillingStore } from '@/stores/billing'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import PaymentMethodSlideIn from '@/screens/trusted/admin/billing/PaymentMethodSlideIn.vue'
import BillingDetailsSlideIn from '@/screens/trusted/admin/billing/BillingDetailsSlideIn.vue'
import { successMessage } from '@/utils/notifications'
import {
    formatMoney,
    formatDeduction,
    priceLabel,
} from '@/helpers/billing-format'
import { describeChange, refusalFix } from '@/helpers/billing-plans'

/**
 * The review step of every plan change: what the API says the change would do,
 * and a button that makes exactly that. Upgrades, downgrades, switching the
 * billing length, cancelling, reactivating, choosing a plan during a trial and
 * starting a trial's plan now all go through here.
 *
 * Opening it asks the API for a preview (which writes nothing). Confirming
 * sends the change back with the preview's hash. If what the API would do has
 * moved on since (the period rolled over, a price changed), it refuses with the
 * new preview, which replaces the one on screen for the owner to look at and
 * confirm again; nothing is applied that they haven't seen.
 */
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    // `{ kind, target }`: what is being changed. `target` is the price it is
    // for (a plan's price at the chosen length, or the Free plan), and is
    // sent only for the kinds that take one. Empty until something is chosen.
    change: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['close', 'done'])

const TARGETED_KINDS = ['change_plan', 'reactivate']

const billingStore = useBillingStore()

// The preview being reviewed, once the API has sent one.
const preview = ref()
const loading = ref(false)
const processing = ref(false)
const acknowledged = ref(false)
const error = ref('')
const errorCode = ref(null)
// The card and billing-details slide-ins open from here, on top of this one.
const inner = ref(null)

// Only the newest preview request counts; an older answer is dropped.
let requestId = 0

const summary = computed(() => billingStore.summary)
const subscription = computed(() => summary.value?.subscription ?? null)
const hasCard = computed(() => !!summary.value?.payment_method?.present)
const target = computed(() => props.change.target ?? null)

const view = computed(() =>
    preview.value
        ? describeChange(preview.value, {
              target: target.value,
              scheduledChange: summary.value?.scheduled_change ?? null,
          })
        : null
)

// A change that charges today can't be confirmed without a card to charge. One
// that only turns into a charge when a trial ends can: the owner can add the
// card now or later, and without one the account moves to Free.
const chargesToday = computed(() => (view.value?.today?.total ?? 0) > 0)
const cardRequired = computed(() => chargesToday.value && !hasCard.value)
const cardSuggested = computed(
    () =>
        view.value?.timing === 'trial' &&
        !!target.value &&
        !target.value.is_free &&
        !hasCard.value
)

const fix = computed(() => refusalFix(errorCode.value))

const canConfirm = computed(
    () =>
        !!preview.value &&
        !loading.value &&
        !processing.value &&
        !cardRequired.value &&
        (!view.value.discountEnds || acknowledged.value)
)

const current = computed(() => {
    const plan = subscription.value?.plan
    return plan ? `${plan.plan_name}, ${priceLabel(plan)}` : null
})

const proposed = computed(() => {
    if (!view.value) return null
    if (view.value.action === 'cancel') return 'Free'

    const price = target.value
    if (!price) return null

    return price.is_free
        ? price.plan_name
        : `${price.plan_name}, ${priceLabel(price)}`
})

// "$49.00 a month" for the plan the next invoice is for.
const nextAmount = computed(() => {
    const next = view.value?.next
    if (!next || next.amount === undefined) return ''

    return `${formatMoney(next.amount, next.currency)} a ${
        next.interval === 'year' ? 'year' : 'month'
    }`
})

const confirmLabel = computed(() => {
    if (!view.value) return 'Confirm'

    if (view.value.action === 'cancel') return 'Cancel my plan'
    if (chargesToday.value) {
        return `Confirm and pay ${formatMoney(
            view.value.today.total,
            view.value.today.currency
        )}`
    }

    return view.value.timing === 'scheduled' ? 'Confirm change' : 'Confirm'
})

function requestBody() {
    const body = { kind: props.change.kind }

    if (TARGETED_KINDS.includes(props.change.kind)) {
        body.target_price_ulid = props.change.target.price_ulid
    }

    return body
}

async function loadPreview() {
    if (!props.change.kind) return

    const id = ++requestId
    loading.value = true
    preview.value = null
    acknowledged.value = false
    error.value = ''
    errorCode.value = null

    try {
        const response =
            await ProfileAccountBillingService.previewChange(requestBody())
        if (id !== requestId) return

        preview.value = response.data
    } catch (e) {
        if (id !== requestId) return

        error.value =
            e?.response?.data?.message ??
            "We couldn't work out what that change would do. Try again."
        errorCode.value = e?.response?.data?.error_code ?? null
    } finally {
        if (id === requestId) loading.value = false
    }
}

const DONE_MESSAGES = {
    now: 'Your plan is updated.',
    scheduled: 'Your change is scheduled.',
    trial: "We've saved your choice for when your trial ends.",
}

async function confirm() {
    if (!canConfirm.value) return

    processing.value = true
    error.value = ''
    errorCode.value = null

    try {
        const response = await ProfileAccountBillingService.commitChange({
            ...requestBody(),
            acknowledged: acknowledged.value,
            hash: preview.value.hash,
        })
        await billingStore.applySummary(response.data)

        successMessage(DONE_MESSAGES[view.value.timing])
        emit('done')
    } catch (e) {
        const body = e?.response?.data
        const newer = body?.data?.preview

        if (e?.response?.status === 409 && newer) {
            // What the API would do has moved on since this preview. Show the
            // new one, and make the owner confirm that instead.
            preview.value = newer
            acknowledged.value = false
            error.value =
                body.error_code === 'preview_stale'
                    ? 'Something changed since you looked. Check the details and confirm again.'
                    : (body.message ?? 'Check the details and confirm again.')
        } else {
            error.value =
                body?.message ?? "We couldn't make that change. Try again."
            errorCode.value = body?.error_code ?? null
        }
    } finally {
        processing.value = false
    }
}

// The card or the billing details were what was missing. Whatever came of the
// slide-in, look at the change again: tax, a retried invoice or a saved card
// can all change what it comes to.
function closeInner() {
    inner.value = null
    if (props.isOpen) void loadPreview()
}

watch(
    () => [props.isOpen, props.change],
    ([open]) => {
        if (open) {
            inner.value = null
            void loadPreview()
        } else {
            requestId += 1
            loading.value = false
            processing.value = false
            preview.value = null
            error.value = ''
            errorCode.value = null
            inner.value = null
        }
    },
    { immediate: true }
)
</script>

<template>
    <div>
        <zen-slide-in
            :is-open="isOpen"
            title="Review your change"
            :error="error"
            @close="emit('close')"
        >
            <template #body>
                <div
                    v-if="loading"
                    class="space-y-4"
                    data-testid="review-loading"
                >
                    <zen-skeleton class="h-16 w-full" />
                    <zen-skeleton class="h-24 w-full" />
                    <zen-skeleton class="h-16 w-full" />
                </div>

                <div
                    v-else-if="!view"
                    class="space-y-4"
                    data-testid="review-unavailable"
                >
                    <p class="text-sm text-taupe-700">
                        We can't go ahead with this change yet.
                    </p>
                    <zen-button
                        v-if="fix"
                        variant="primary"
                        :data-testid="`review-fix-${fix.action}`"
                        @click="inner = fix.action"
                    >
                        {{ fix.label }}
                    </zen-button>
                </div>

                <div v-else class="space-y-6" data-testid="review">
                    <section
                        class="rounded-lg bg-taupe-50 p-4 text-sm"
                        data-testid="review-summary"
                    >
                        <div class="text-taupe-500">Now</div>
                        <div
                            class="font-medium text-taupe-900"
                            data-testid="review-current"
                        >
                            {{ current }}
                        </div>
                        <div class="mt-3 text-taupe-500">After this change</div>
                        <div
                            class="font-medium text-taupe-900"
                            data-testid="review-proposed"
                        >
                            {{ proposed }}
                        </div>
                    </section>

                    <section data-testid="review-today">
                        <h3 class="text-sm font-semibold text-taupe-900">
                            Today
                        </h3>
                        <template v-if="view.today">
                            <dl class="mt-2 space-y-1 text-sm">
                                <div
                                    v-for="line in view.today.lines"
                                    :key="line.key"
                                    class="flex justify-between gap-4"
                                    :data-testid="`review-line-${line.key}`"
                                >
                                    <dt class="text-taupe-600">
                                        {{ line.label }}
                                    </dt>
                                    <dd class="text-taupe-900">
                                        {{
                                            line.amount < 0
                                                ? formatDeduction(
                                                      line.amount,
                                                      view.today.currency
                                                  )
                                                : formatMoney(
                                                      line.amount,
                                                      view.today.currency
                                                  )
                                        }}
                                    </dd>
                                </div>
                                <div
                                    class="flex justify-between gap-4 border-t border-taupe-200 pt-2 font-medium text-taupe-900"
                                    data-testid="review-total"
                                >
                                    <dt>
                                        {{
                                            view.today.total < 0
                                                ? 'Added to your credit'
                                                : 'Charged today'
                                        }}
                                    </dt>
                                    <dd>
                                        {{
                                            formatMoney(
                                                Math.abs(view.today.total),
                                                view.today.currency
                                            )
                                        }}
                                    </dd>
                                </div>
                            </dl>
                            <p
                                v-if="view.today.discountOff > 0"
                                class="mt-2 text-xs text-taupe-500"
                                data-testid="review-discount-applied"
                            >
                                Includes
                                {{
                                    formatMoney(
                                        view.today.discountOff,
                                        view.today.currency
                                    )
                                }}
                                off from your discount.
                            </p>
                            <p
                                v-if="view.today.reverseCharge"
                                class="mt-2 text-xs text-taupe-500"
                            >
                                Tax is reverse charged, so none is added.
                            </p>
                        </template>
                        <p
                            v-else
                            class="mt-2 text-sm text-taupe-600"
                            data-testid="review-nothing-today"
                        >
                            Nothing to pay today.
                        </p>
                    </section>

                    <section data-testid="review-next">
                        <h3 class="text-sm font-semibold text-taupe-900">
                            After that
                        </h3>
                        <p
                            v-if="view.next.kind === 'free'"
                            class="mt-2 text-sm text-taupe-700"
                            data-testid="review-next-free"
                        >
                            On
                            <zen-date-time
                                :value="view.next.date"
                                format="date"
                                utc
                            />
                            you move to Free. There's nothing to pay after that.
                        </p>
                        <template v-else>
                            <p
                                v-if="view.next.kind === 'moves'"
                                class="mt-2 text-sm text-taupe-700"
                            >
                                On
                                <zen-date-time
                                    :value="view.next.date"
                                    format="date"
                                    utc
                                />
                                you move to
                                {{ view.next.name ?? 'the new plan' }}. Until
                                then you keep your current plan. Your next
                                invoice is for
                                <span
                                    class="font-medium text-taupe-900"
                                    data-testid="review-next-amount"
                                    >{{ nextAmount }}</span
                                >.
                            </p>
                            <p
                                v-else-if="view.next.kind === 'first-invoice'"
                                class="mt-2 text-sm text-taupe-700"
                            >
                                When your trial ends on
                                <zen-date-time
                                    :value="view.next.date"
                                    format="date"
                                    utc
                                />, your first invoice is for
                                <span
                                    class="font-medium text-taupe-900"
                                    data-testid="review-next-amount"
                                    >{{ nextAmount }}</span
                                >.
                            </p>
                            <p v-else class="mt-2 text-sm text-taupe-700">
                                Your next invoice is on
                                <zen-date-time
                                    :value="view.next.date"
                                    format="date"
                                    utc
                                />, for
                                <span
                                    class="font-medium text-taupe-900"
                                    data-testid="review-next-amount"
                                    >{{ nextAmount }}</span
                                >.
                            </p>
                            <p class="mt-1 text-xs text-taupe-500">
                                That's the plan's price. Any discount, credit
                                and tax are worked out when the invoice is
                                issued.
                            </p>
                        </template>
                    </section>

                    <section
                        v-if="view.lostFeatures.length"
                        class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                        data-testid="review-lost"
                    >
                        <div class="font-medium">You'd lose</div>
                        <ul class="mt-1 list-disc space-y-0.5 pl-5">
                            <li
                                v-for="feature in view.lostFeatures"
                                :key="feature"
                            >
                                {{ feature }}
                            </li>
                        </ul>
                    </section>

                    <p
                        v-if="view.overLimit"
                        class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                        data-testid="review-over-limit"
                    >
                        You've used
                        {{ view.overLimit.usage.toLocaleString('en-US') }}
                        monthly active contexts this period, over the
                        {{ view.overLimit.limit.toLocaleString('en-US') }} this
                        plan includes. We never bill overage, and nothing is
                        deleted.
                    </p>

                    <p
                        v-if="view.replacesScheduled"
                        class="text-sm text-taupe-600"
                        data-testid="review-replaces-scheduled"
                    >
                        This replaces the change scheduled for
                        <zen-date-time
                            :value="view.replacesScheduled.effectiveAt"
                            format="date"
                            utc
                        />.
                    </p>

                    <ul
                        v-if="view.notices.length"
                        class="list-disc space-y-1 pl-5 text-sm text-taupe-600"
                        data-testid="review-notices"
                    >
                        <li v-for="notice in view.notices" :key="notice">
                            {{ notice }}
                        </li>
                    </ul>

                    <label
                        v-if="view.discountEnds"
                        class="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                    >
                        <input
                            v-model="acknowledged"
                            type="checkbox"
                            class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                            data-testid="review-acknowledge"
                        />
                        <span
                            >{{ view.discountEnds.notice }} I understand it
                            can't be brought back.</span
                        >
                    </label>

                    <section
                        v-if="cardRequired || cardSuggested"
                        class="flex items-center justify-between gap-3 rounded-lg bg-taupe-50 p-4 text-sm text-taupe-700"
                        data-testid="review-add-card"
                    >
                        <span>
                            <template v-if="cardRequired"
                                >Add a card to pay for this.</template
                            >
                            <template v-else
                                >Add a card now so your first invoice goes
                                through.</template
                            >
                        </span>
                        <zen-button
                            variant="secondary"
                            size="small"
                            data-testid="review-fix-card"
                            @click="inner = 'card'"
                        >
                            Add a card
                        </zen-button>
                    </section>

                    <div v-if="fix" class="flex justify-end">
                        <zen-button
                            variant="secondary"
                            size="small"
                            :data-testid="`review-fix-${fix.action}`"
                            @click="inner = fix.action"
                        >
                            {{ fix.label }}
                        </zen-button>
                    </div>
                </div>
            </template>

            <template #buttons>
                <zen-button
                    type="button"
                    variant="secondary"
                    @click="emit('close')"
                >
                    Back
                </zen-button>
                <zen-button
                    type="button"
                    :variant="
                        view?.action === 'cancel' ? 'primary-danger' : 'primary'
                    "
                    :disabled="!canConfirm"
                    :processing="processing"
                    data-testid="review-confirm"
                    @click="confirm"
                >
                    {{ confirmLabel }}
                </zen-button>
            </template>
        </zen-slide-in>

        <payment-method-slide-in
            :is-open="inner === 'card'"
            @close="closeInner"
        />
        <billing-details-slide-in
            :is-open="inner === 'details'"
            @close="closeInner"
        />
    </div>
</template>
